import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { set } from 'idb-keyval'
import { mutate } from 'swr'

import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Separator } from '@components/ui/separator'
import { Heading } from '@components/ui/typography/heading'
import { Text } from '@components/ui/typography/text'
import { VStack, Flex } from '@components/ui/layout/flex'
import { Label } from '@components/ui/label'

import { updatePassword, getMe, useBranding } from '@utils/api'
import { ApiError } from '@utils/api/helpers'
import { AboboCrypto } from '@utils/aboboCrypto'
import Loading from '@components/Loading'

type UpdatePasswordSearch = {
  token?: string
  locale?: string
}

export const Route = createFileRoute('/secure/updatePassword')({
  component: UpdatePassword,
  validateSearch: (search: Record<string, unknown>): UpdatePasswordSearch => {
    return {
      token: search.token as string | undefined,
      locale: search.locale as string | undefined,
    }
  },
})

function UpdatePassword() {
  const navigate = useNavigate()
  const { token, locale } = useSearch({ from: '/secure/updatePassword' })
  const { branding, isLoading, isError } = useBranding()
  const [success, setSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (locale) i18n.changeLanguage(locale)
  }, [i18n, locale])

  const {
    register,
    reset,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  })

  const toggleShowPassword = () => setShowPassword((currentValue) => !currentValue)

  const onSubmit = async (data: any) => {
    try {
      const result = await updatePassword({
        password: data.password,
        'password-confirm': data.passwordConfirmation,
        key: token,
      })

      if (result.error) {
        toast.error(t('secure.updatePassword.errors.linkInvalidTitle'), {
          description: result.error,
        })
        return
      }

      // Backend auto-logged us in - initialize crypto keys
      try {
        const userResponse = await getMe()
        const userData = userResponse.data

        // Prime SWR cache so AuthContext recognizes the session immediately
        mutate('/patients/me', userResponse, { revalidate: false })

        if (userData?.activeKey) {
          // Derive encryption key from password and initialize crypto keys
          const patientDecryptionKey = await AboboCrypto.deriveKey(
            data.password,
            userData.activeKey.salt
          )

          const [privateKey, publicKey, clientKey] = await Promise.all([
            AboboCrypto.loadClientPrivateKey(userData.activeKey.privateKey, patientDecryptionKey),
            AboboCrypto.importPublicKey(userData.activeKey.publicKey),
            AboboCrypto.importPublicKey(userData.clientPublicKey),
          ])

          // Store keys in IndexedDB
          await Promise.all([
            set('patientPrivateKey', privateKey),
            set('patientPublicKey', publicKey),
            set('clientPublicKey', clientKey),
          ])
        }
      } catch (keyError) {
        console.error('Failed to initialize crypto keys:', keyError)
      }

      // Show success screen - button will navigate to secure area
      setSuccess(true)
      reset()
    } catch (error: unknown) {
      // Provide specific error messages based on error type
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 422) {
          // Token expired or invalid
          toast.error(t('secure.updatePassword.errors.linkExpiredTitle'), {
            description: t('secure.updatePassword.errors.linkExpiredMessage'),
          })
        } else if (error.status >= 500) {
          // Server error
          toast.error(t('secure.updatePassword.errors.serverErrorTitle'), {
            description: t('secure.updatePassword.errors.serverErrorMessage'),
          })
        } else {
          // Other API error - show the error message from server
          toast.error(t('secure.updatePassword.errors.linkInvalidTitle'), {
            description: error.getDisplayMessage(),
          })
        }
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        // Network error
        toast.error(t('secure.updatePassword.errors.networkErrorTitle'), {
          description: t('secure.updatePassword.errors.networkErrorMessage'),
        })
      } else {
        // Unknown error
        toast.error(t('toasts.errorTitle'), {
          description: t('toasts.somethingWentWrong'),
        })
      }
    }
  }

  const password = watch('password')

  if (isLoading) return <Loading />
  if (isError) {
    navigate({ to: '/' })
    return null
  }

  // Use min-h-[100svh] with flex-start vertical alignment to ensure content
  // is accessible on small mobile screens. The py-8 provides visual balance.
  return (
    <Flex
      direction="column"
      align="center"
      justify="start"
      className="min-h-[100svh] py-8 px-4"
    >
      <VStack
        className="w-full max-w-sm bg-white p-8 rounded-[15px]"
        align="stretch"
        gap={4}
      >
        <img
          alt={`${branding?.name}'s Logo`}
          src={branding?.logo}
          className="h-20 object-contain"
        />

        {success && (
          <div className="space-y-4">
            <Heading level={4} className="py-2">
              {t('secure.updatePassword.successTitle')}
            </Heading>
            <Text>{t('secure.updatePassword.successMessage')}</Text>
            <Text className="font-extrabold text-center"> . . . </Text>
            <Button
              style={{
                color: branding?.fontColor,
                backgroundColor: branding?.buttonColor,
              }}
              className="w-full hover:-translate-y-0.5 hover:shadow-lg transition-all"
              onClick={() => navigate({ to: '/secure' })}
            >
              {t('secure.updatePassword.buttons.getBackIn')}
            </Button>
          </div>
        )}

        {!success && (
          <div className="space-y-4">
            <Heading level={4} className="py-2">
              {t('secure.updatePassword.title')}
            </Heading>
            <Separator />
            <Text>{t('secure.updatePassword.requirements.title')}</Text>
            <ul className="space-y-1">
              <li className="flex items-center gap-2">
                {password.length >= 8 ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <Text size="sm">{t('secure.updatePassword.requirements.minLength')}</Text>
              </li>
              <li className="flex items-center gap-2">
                {password.length > 0 && password.length <= 64 ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <Text size="sm">{t('secure.updatePassword.requirements.maxLength')}</Text>
              </li>
              <li className="flex items-center gap-2">
                {/[a-z]/.test(password) ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <Text size="sm">{t('secure.updatePassword.requirements.lowercase')}</Text>
              </li>
              <li className="flex items-center gap-2">
                {/[A-Z]/.test(password) ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <Text size="sm">{t('secure.updatePassword.requirements.uppercase')}</Text>
              </li>
              <li className="flex items-center gap-2">
                {/\d/.test(password) ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <Text size="sm">{t('secure.updatePassword.requirements.number')}</Text>
              </li>
              <li className="flex items-center gap-2">
                {/[!@#$%^&*()\-,.?":{}|<>]/.test(password) ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <Text size="sm">{t('secure.updatePassword.requirements.special')}</Text>
              </li>
            </ul>
            <Separator />

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1">
                <Label className="font-medium">
                  {t('secure.updatePassword.fieldNames.password')}
                </Label>
                <div className="relative">
                  <Input
                    {...register('password', {
                      required: t('secure.updatePassword.errors.passwordRequired'),
                      validate: {
                        hasUpperCase: (value) =>
                          /[A-Z]/.test(value) ||
                          t('secure.updatePassword.errors.missingUppercase'),
                        hasLowerCase: (value) =>
                          /[a-z]/.test(value) ||
                          t('secure.updatePassword.errors.missingLowercase'),
                        hasNumber: (value) =>
                          /[0-9]/.test(value) ||
                          t('secure.updatePassword.errors.missingNumber'),
                        hasSymbol: (value) =>
                          /[!@#$%^&*()\-,.?":{}|<>]/.test(value) ||
                          t('secure.updatePassword.errors.missingSpecial'),
                        minLength: (value) =>
                          value.length >= 8 ||
                          t('secure.updatePassword.errors.passwordTooShort'),
                      },
                    })}
                    placeholder="e.g. ********"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    aria-invalid={!!errors.password}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={toggleShowPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <Text size="sm" className="text-destructive">
                    {errors.password.message}
                  </Text>
                )}
              </div>

              <div className="space-y-1">
                <Label className="font-medium">
                  {t('secure.updatePassword.fieldNames.confirmPassword')}
                </Label>
                <div className="relative">
                  <Input
                    {...register('passwordConfirmation', {
                      required: t('secure.updatePassword.errors.passwordRequired'),
                      validate: (value, formValues) =>
                        value === formValues.password || t('secure.updatePassword.errors.passwordsDontMatch'),
                    })}
                    placeholder="e.g. ********"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    aria-invalid={!!errors.passwordConfirmation}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={toggleShowPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.passwordConfirmation && (
                  <Text size="sm" className="text-destructive">
                    {errors.passwordConfirmation.message}
                  </Text>
                )}
              </div>

              <Button
                type="submit"
                style={{
                  color: branding?.fontColor,
                  backgroundColor: branding?.buttonColor,
                }}
                className="w-full mt-2 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                loading={isSubmitting}
              >
                {t('secure.updatePassword.buttons.updatePassword')}
              </Button>
            </form>
          </div>
        )}
      </VStack>
    </Flex>
  )
}
